import {FC, SyntheticEvent, useState} from 'react';
import {useCan} from 'src/permissions/hooks';
import {Edit, NotesOutlined} from '@mui/icons-material';
import {Box, Button, Tab, Tabs, Typography} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {FormattedMessage, useIntl} from 'react-intl';
import AMPModal from 'src/components/common/AMPModal';
import {TariffDescriptionForm} from 'src/components/forms/TariffDescriptionForm';
import {TariffDescriptionType} from './TariffDescription.types';
import {
    descriptionBodySx,
    detailsWrapperSx,
    panelStackSx,
    panelSx,
    markdownSx,
    sectionBadgeSx,
    sectionHeaderSx,
    sectionTitleSx,
    tabsBandSx,
    tabsSx,
    tabSx,
} from './TariffDescription.sx';
import {actionButtonSx} from 'src/components/common/actionButton.sx';

export const TariffDescription: FC<TariffDescriptionType> = ({data}) => {
    const intl = useIntl();
    const canUpdateTariff = useCan('tariffs.update');
    const editDescriptionTitle = intl.formatMessage({
        id: 'editDescriptionTitle',
    });
    const [tab, setTab] = useState(0);
    const [open, setOpen] = useState<boolean>(false);
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleTab = (_event: SyntheticEvent, newValue: number) => {
        setTab(newValue);
    };

    const descriptions = [
        data?.description ?? '',
        data?.descriptionFrench ?? '',
        data?.descriptionDeutsch ?? '',
    ];

    return (
        <>
            <Box sx={detailsWrapperSx}>
                <Box sx={sectionHeaderSx}>
                    <Box sx={sectionBadgeSx}>
                        <NotesOutlined/>
                    </Box>
                    <Typography sx={sectionTitleSx}>
                        <FormattedMessage id="description"/>
                    </Typography>
                    <Box sx={{flex: '1 1 auto'}}/>
                    {/* Same permission as before — only the button's shape changed. An icon alone
                        left the reader guessing which of the three languages it would edit. */}
                    {canUpdateTariff && (
                        <Button
                            variant="contained"
                            sx={actionButtonSx('32px', 'secondary')}
                            startIcon={<Edit/>}
                            onClick={handleOpen}
                        >
                            <FormattedMessage id="edit"/>
                        </Button>
                    )}
                </Box>
                <Tabs
                    value={tab}
                    onChange={handleTab}
                    sx={{...tabsBandSx as object, ...tabsSx as object}}
                >
                    <Tab
                        sx={tabSx}
                        label={intl.formatMessage({id: 'english'})}
                    />
                    <Tab
                        sx={tabSx}
                        label={intl.formatMessage({id: 'french'})}
                    />
                    <Tab
                        sx={tabSx}
                        label={intl.formatMessage({id: 'german'})}
                    />
                </Tabs>
                {/* All three translations are laid on top of each other in one grid cell, and the
                    inactive ones are hidden rather than unmounted. Rendering only the active one made
                    the card as tall as whichever language was showing, so switching tabs resized the
                    section and shifted the client table below it by a few pixels. */}
                <Box sx={panelStackSx}>
                    {descriptions.map((text, index) => (
                        <Typography
                            key={index}
                            variant="body1"
                            component="div"
                            aria-hidden={tab !== index}
                            sx={{
                                ...descriptionBodySx as object,
                                ...markdownSx as object,
                                ...panelSx as object,
                                visibility: tab === index ? 'visible' : 'hidden',
                            }}
                        >
                            <ReactMarkdown>{text}</ReactMarkdown>
                        </Typography>
                    ))}
                </Box>
            </Box>
            <AMPModal
                handleClose={handleClose}
                open={open}
                title={editDescriptionTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <TariffDescriptionForm
                    tariffId={data.id}
                    businessId={data.business.id}
                    monthlyFee={data.monthlyFee}
                    currency={data.currency}
                    description={data.description}
                    descriptionFrench={data.descriptionFrench}
                    descriptionGerman={data.descriptionDeutsch}
                    handleClose={handleClose}
                    country={data.country}
                    region={data.region}
                />
            </AMPModal>
        </>
    );
};
